import { CompanyInfoBadge } from '@aibees/elephant/components/company-info-badge';
import { Grid2, Icon, InternalLinkButton } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Avatar, Card, CardContent, Divider } from '@mui/material';
import { memo, useMemo } from 'react';
import { getContactDetailsCompanyLink } from '../../../../utils';
import { WorkHistoryCompany } from '../work-history-company';
import { WorkHistoryDate } from '../work-history-date';
import { WorkHistoryDuration } from '../work-history-duration';
import { WorkHistoryExpandRolesButton } from '../work-history-expand-roles-button';
import { WorkHistoryTimeline } from '../work-history-timeline';
import { WorkHistoryTitle } from '../work-history-title';
import * as SX from './sx';
import type { WorkHistoryItemProps as Props } from './types';
import { doesWorkHistoryItemHasMoreThanOneRole } from './utils';

export const WorkHistoryItem = memo(
  ({ className, item, isLastItem, personData }: Props) => {
    const rolesExpanded = useBoolean(false);
    const companyLink = getContactDetailsCompanyLink(item.company);
    const hasMoreThanOneRole = doesWorkHistoryItemHasMoreThanOneRole(item);

    const companyAvatar = useMemo(
      () => (
        <Avatar
          size={56}
          src={item.company?.logo ?? undefined}
          color="white"
          variant="rounded"
        >
          <Icon size={20} name="business" color="grey.400" />
        </Avatar>
      ),
      [item.company?.logo]
    );

    return (
      <Card
        className={classnames('WorkHistoryItem', className)}
        sx={SX.root}
        variant="filled"
        color="default"
      >
        <CardContent padding="extra">
          <Grid2
            container
            columns={48}
            rowSpacing={1}
            columnSpacing={1}
            sx={SX.mainContent}
          >
            <Grid2
              xs={48}
              sm={48}
              md={hasMoreThanOneRole ? 40 : 48}
              lg={hasMoreThanOneRole ? 40 : 48}
              xl={hasMoreThanOneRole ? 40 : 48}
              p={0}
            >
              <Grid2 sx={{ minWidth: 'fit-content' }}>
                <InternalLinkButton to={companyLink}>
                  {isLastItem ? (
                    <CompanyInfoBadge badgeSize={22} personData={personData}>
                      {companyAvatar}
                    </CompanyInfoBadge>
                  ) : (
                    companyAvatar
                  )}
                </InternalLinkButton>
              </Grid2>

              <Grid2 sx={SX.description}>
                <InternalLinkButton to={companyLink}>
                  <WorkHistoryCompany item={item} />
                </InternalLinkButton>
                <WorkHistoryTitle item={item} />

                {item.date?.start && (
                  <Grid2>
                    <WorkHistoryDate item={item} fontWeight={400} />
                    <Divider flexItem variant="middle" orientation="vertical" />
                    <WorkHistoryDuration item={item} />
                  </Grid2>
                )}
              </Grid2>
            </Grid2>

            {hasMoreThanOneRole && item.profile_positions && (
              <Grid2 md={8} lg={8} xl={8} sx={SX.role}>
                <WorkHistoryExpandRolesButton
                  items={item.profile_positions}
                  state={rolesExpanded}
                />
              </Grid2>
            )}
          </Grid2>

          {rolesExpanded.value && item.profile_positions && (
            <Grid2 xs={48}>
              <WorkHistoryTimeline
                items={item.profile_positions}
                state={rolesExpanded}
              />
            </Grid2>
          )}
        </CardContent>
      </Card>
    );
  }
);

WorkHistoryItem.displayName = 'WorkHistoryItem';
